/**
 * TanStack Query hooks for per-operator map annotations — the BFF
 * ``/api/annotations`` CRUD (ADR-034; feature-spec §5.2). REQ-UI-106
 * (per-operator persistence); REQ-UI-035 (BFF-only, same-origin).
 *
 * - :func:`useAnnotations` — the caller's annotations (GET list).
 * - :func:`useCreateAnnotation` / :func:`useUpdateAnnotation` /
 *   :func:`useDeleteAnnotation` / :func:`useClearAnnotations` — the mutations,
 *   each invalidating the list query on success so the map + any list re-read
 *   the server-authoritative state.
 *
 * The annotation set is small (per-operator, tens of shapes) and private, so
 * there is no SSE — a plain query + invalidate-on-mutate is sufficient
 * (feature-spec §5.3). All calls go through :func:`apiFetch` (same-origin,
 * CSRF on mutations, 401→login).
 *
 * Cross-references:
 *   - ADR-034; feature-spec map-annotations-and-measurement.md §5.2.
 *   - REQ-UI-106 (persistence) / REQ-UI-035 (BFF-only).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";

/** A persisted annotation as returned by the BFF. */
export type Annotation = components["schemas"]["Annotation"];
/** The create/update body — the mutable annotation fields. */
export type AnnotationWriteRequest = components["schemas"]["AnnotationWriteRequest"];

const ANNOTATIONS_PATH = "/api/annotations";

/** The query key for the caller's annotation list. */
export const annotationsQueryKey = ["annotations"] as const;

/** Fetch the caller's annotations (GET ``/api/annotations``). */
async function fetchAnnotations(signal?: AbortSignal): Promise<Annotation[]> {
  return apiFetch<Annotation[]>(ANNOTATIONS_PATH, { signal });
}

/** The caller's annotations. Refetches on window focus per the app default. */
export function useAnnotations(): UseQueryResult<Annotation[], ApiError> {
  return useQuery<Annotation[], ApiError>({
    queryKey: annotationsQueryKey,
    queryFn: ({ signal }) => fetchAnnotations(signal),
  });
}

function annotationPath(annotationId: string): string {
  return `${ANNOTATIONS_PATH}/${encodeURIComponent(annotationId)}`;
}

/** Create a new annotation (POST). Invalidates the list on success. */
export function useCreateAnnotation(): UseMutationResult<
  Annotation,
  ApiError,
  AnnotationWriteRequest
> {
  const queryClient = useQueryClient();
  return useMutation<Annotation, ApiError, AnnotationWriteRequest>({
    mutationFn: (body) =>
      apiFetch<Annotation>(ANNOTATIONS_PATH, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: annotationsQueryKey });
    },
  });
}

/** Variables for an annotation update (the id + the full replacement body). */
export interface UpdateAnnotationVariables {
  readonly annotationId: string;
  readonly body: AnnotationWriteRequest;
}

/** Replace an annotation's geometry/style/label (PUT). Invalidates on success. */
export function useUpdateAnnotation(): UseMutationResult<
  Annotation,
  ApiError,
  UpdateAnnotationVariables
> {
  const queryClient = useQueryClient();
  return useMutation<Annotation, ApiError, UpdateAnnotationVariables>({
    mutationFn: ({ annotationId, body }) =>
      apiFetch<Annotation>(annotationPath(annotationId), {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: annotationsQueryKey });
    },
  });
}

/** Delete one annotation by id (DELETE). Invalidates on success. */
export function useDeleteAnnotation(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (annotationId) =>
      apiFetch<void>(annotationPath(annotationId), { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: annotationsQueryKey });
    },
  });
}

/** Clear ALL the caller's annotations (DELETE collection). Invalidates on success. */
export function useClearAnnotations(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => apiFetch<void>(ANNOTATIONS_PATH, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: annotationsQueryKey });
    },
  });
}
